import {
  Editor,
  Node,
  ResizableNodeView,
} from '@tiptap/core';
import { maxLogoHeight, maxLogoWidth } from 'shared';

const senderBlockTags = [`Logo`, `Signature`];

const tagOnly = new RegExp(`^\\{\\{\\{(${senderBlockTags.join(`|`)})\\}\\}\\}$`);

// The logo starts centered, where the fixed logo has always been
export const insertSenderBlock = (editor: Editor, tag: string) => editor.chain().focus()
  .insertContent({ type: `senderBlock`, attrs: { tag, textAlign: tag === `Logo` ? `center` : null } })
  .run();

// {{{Logo}}} or {{{Signature}}} as one block, so it can't be typed into or split, and lines up with the
// TextAlign buttons. Saved as a plain paragraph holding only the tag, which generateHtml swaps for the
// sender's logo or signature
export const SenderBlockNode = Node.create<{ logoUrl?: string | null }>({
  name: `senderBlock`,
  group: `block`,
  atom: true,
  selectable: true,
  draggable: true,
  addOptions: () => ({ logoUrl: null }),
  addAttributes: () => ({
    // Kept in the tag text, not as its own html attribute
    tag: { default: null, rendered: false },
    // Logo only, set by dragging its handles. Height follows each sender's logo
    width: {
      default: null,
      parseHTML: el => Number(el.getAttribute(`data-width`)) || null,
      renderHTML: ({ width }) => (width ? { 'data-width': width } : {}),
    },
  }),
  // Ahead of the paragraph, so a paragraph holding only the tag (typed, pasted or AI written) becomes the block
  parseHTML: () => [{
    tag: `p`,
    priority: 60,
    getAttrs: el => {
      const tag = el.textContent?.trim().match(tagOnly)?.[1];

      return tag ? { tag } : false;
    },
  }],
  renderHTML: ({ node, HTMLAttributes }) => [`p`, HTMLAttributes, `{{{${node.attrs.tag}}}}`],
  renderText: ({ node }) => `{{{${node.attrs.tag}}}}`,
  // The logo is drawn as the sender's logo you can resize, anything else (or no logo) as a merge tag pill.
  // Either way the saved html stays a bare tag
  addNodeView() {
    const { logoUrl } = this.options;

    return ({ node, HTMLAttributes, editor, getPos }) => {
      if (node.attrs.tag === `Logo` && logoUrl) {
        const img = document.createElement(`img`);

        img.src = logoUrl;

        const view = new ResizableNodeView({
          element: img,
          editor,
          node,
          getPos,
          onResize: width => {
            img.style.maxWidth = `min(100%, ${width}px)`;
          },
          onCommit: width => {
            const pos = getPos();

            if (pos === undefined) return;
            editor.chain().setNodeSelection(pos).updateAttributes(`senderBlock`, { width: Math.round(width) }).run();
          },
          onUpdate: updated => {
            if (updated.attrs.tag !== `Logo`) return false;
            show(updated);

            return true;
          },
          options: {
            directions: [`left`, `right`, `bottom-left`, `bottom-right`],
            min: { width: 40 },
            max: { width: parseInt(maxLogoWidth), height: parseInt(maxLogoHeight) },
            preserveAspectRatio: true,
          },
        });

        // Logos are stored resized to fit 560x150, so never scale past that or the file's own size,
        // either would pixelate
        img.onload = () => {
          view.maxSize = { width: Math.min(img.naturalWidth, parseInt(maxLogoWidth)), height: Math.min(img.naturalHeight, parseInt(maxLogoHeight)) };
        };

        // Also on undo and the align buttons. Sized like the sent email: the picked width is a max, not a stretch.
        // Also capped at the editor's width, which can be narrower than the 560px email column
        const show = (n: typeof node) => {
          img.style.width = ``;
          img.style.maxWidth = `min(100%, ${n.attrs.width ? `${n.attrs.width}px` : maxLogoWidth})`;
          img.style.maxHeight = maxLogoHeight;
          view.dom.style.justifyContent = ({ left: `flex-start`, right: `flex-end` } as Record<string, string>)[n.attrs.textAlign] ?? `center`;
        };

        view.dom.setAttribute(`data-sender-block`, ``);
        show(node);

        return view;
      }

      const dom = document.createElement(`p`);
      const pill = document.createElement(`span`);

      Object.entries(HTMLAttributes).forEach(([key, value]) => dom.setAttribute(key, value));
      // Editor only hook for the hover and selected styles in editor.css
      dom.setAttribute(`data-sender-block`, ``);
      pill.className = `merge-tag`;
      pill.textContent = `{{{${node.attrs.tag}}}}`;
      dom.append(pill);

      return { dom };
    };
  },
});
